import { useEffect, useRef } from 'react';
import { useRevealOnScroll } from '@/hooks/useRevealOnScroll';

export default function CTASection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;
    const handleScroll = () => {
      const rect = section.getBoundingClientRect();
      const progress = Math.max(0, Math.min(1, 1 - (rect.top / window.innerHeight)));
      section.style.setProperty('--scroll-progress', String(progress));
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useRevealOnScroll(sectionRef);

  return (
    <section
      id="contact"
      ref={sectionRef}
      className="relative w-full min-h-[70vh] md:min-h-[80vh] flex items-center justify-center overflow-hidden bg-background-100"
    >
      {/* Section transition marker */}
      <div className="absolute top-0 left-0 right-0 flex items-center justify-center gap-3 z-20">
        <div className="w-10 h-[1px] bg-gradient-to-r from-transparent to-accent-500/20"></div>
        <div className="w-1 h-1 rounded-full bg-accent-500/25"></div>
        <div className="w-10 h-[1px] bg-gradient-to-l from-transparent to-accent-500/20"></div>
      </div>

      {/* Background texture */}
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Abstract%20mineral%20stone%20crystalline%20texture%20with%20soft%20golden%20mica%20light%20radiating%20from%20center%20through%20translucent%20geometric%20forms%20pale%20warm%20stone%20grey%20background%20delicate%20silvery%20gold%20veins%20converging%20toward%20middle%20organic%20flow%20minimal%20subtle%20texture%20no%20text%20editorial%20photography%20very%20light%20ethereal%20atmosphere%20ceremonial%20feel&width=1600&height=900&seq=cta-bg-stone-01&orientation=landscape"
          alt=""
          className="w-full h-full object-cover object-top opacity-20"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-background-100 via-background-100/95 to-background-100"></div>
      </div>

      {/* Floating amber particles */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-[10%] left-[12%] w-1 h-1 rounded-full bg-accent-500/30 animate-float" style={{ animationDelay: '0s' }}></div>
        <div className="absolute top-[25%] right-[18%] w-1.5 h-1.5 rounded-full bg-accent-400/25 animate-float" style={{ animationDelay: '2s', animationDuration: '10s' }}></div>
        <div className="absolute bottom-[20%] left-[20%] w-1 h-1 rounded-full bg-accent-500/20 animate-float-slow" style={{ animationDelay: '3.5s' }}></div>
        <div className="absolute bottom-[30%] right-[12%] w-1.5 h-1.5 rounded-full bg-accent-400/28 animate-float" style={{ animationDelay: '1s', animationDuration: '9s' }}></div>
        <div className="absolute top-[50%] left-[6%] w-1 h-1 rounded-full bg-accent-500/22 animate-float" style={{ animationDelay: '4s' }}></div>
        <div className="absolute top-[65%] right-[8%] w-1 h-1 rounded-full bg-accent-400/20 animate-float-slow" style={{ animationDelay: '5.5s' }}></div>
      </div>

      {/* Ambient glow orbs — scroll driven */}
      <div className="absolute inset-0 pointer-events-none">
        <div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-accent-500/2 blur-[180px] animate-breathe"
          style={{ opacity: 'calc(0.3 + var(--scroll-progress, 0) * 0.4)', animationDuration: '12s' }}
        ></div>
        <div
          className="absolute bottom-1/4 left-1/3 w-[350px] h-[350px] rounded-full bg-accent-400/2 blur-[140px] animate-breathe"
          style={{ opacity: 'calc(0.2 + var(--scroll-progress, 0) * 0.3)', animationDuration: '8s', animationDelay: '3s' }}
        ></div>
      </div>

      {/* Concentric decorative rings — ceremonial */}
      <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
        <div className="absolute w-[500px] h-[500px] animate-breathe" style={{ animationDuration: '12s' }}>
          <div
            className="w-full h-full rounded-full border border-accent-500/6 animate-rotate-slow opacity-40"
            style={{ animationDuration: '35s' }}
          ></div>
        </div>
        <div className="absolute w-[380px] h-[380px] animate-breathe" style={{ animationDuration: '8s', animationDelay: '2s' }}>
          <div
            className="w-full h-full rounded-full border border-accent-500/5 animate-rotate-slow opacity-35"
            style={{ animationDirection: 'reverse', animationDuration: '28s' }}
          ></div>
        </div>
        <div className="absolute w-[260px] h-[260px] animate-breathe" style={{ animationDuration: '10s', animationDelay: '4.5s' }}>
          <div
            className="w-full h-full rounded-full border border-accent-500/4 animate-rotate-slow opacity-25"
            style={{ animationDuration: '22s' }}
          ></div>
        </div>
      </div>

      {/* Content */}
      <div className="relative z-10 w-full px-6 md:px-12 py-20 md:py-28">
        <div className="max-w-4xl mx-auto text-center">
          {/* Eyebrow */}
          <div data-reveal className="flex items-center justify-center gap-3 mb-8">
            <div className="w-8 h-[1px] bg-accent-500/40"></div>
            <span className="text-xs font-label tracking-[0.3em] uppercase text-accent-500">
              Let&apos;s Build
            </span>
            <div className="w-8 h-[1px] bg-accent-500/40"></div>
          </div>

          {/* Main heading */}
          <h2 data-reveal data-reveal-delay="0.1" className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-900 tracking-tight leading-[1.15]">
            <span className="text-shimmer">Stop configuring</span> around
            <br className="hidden md:block" />
            software built for someone else.
          </h2>

          <p data-reveal data-reveal-delay="0.2" className="mt-6 text-sm md:text-base text-foreground-700 max-w-xl mx-auto leading-relaxed">
            Generate the system your business actually needs.
            <br />
            Tell us what you&apos;re building.
          </p>

          {/* CTA button */}
          <a
            data-reveal
            data-reveal-delay="0.65"
            href="mailto:Contact@generativetech.ai"
            className="inline-flex items-center gap-3 mt-14 px-10 py-4 rounded-full bg-accent-500 text-background-950 font-semibold text-sm md:text-base hover:bg-accent-400 hover:scale-105 hover:ring-4 hover:ring-accent-400/30 transition-all duration-300 whitespace-nowrap cursor-pointer glow-ring animate-breathe-cta"
          >
            Get in Touch
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-line"></i>
            </div>
          </a>

          {/* Bottom hint */}

        </div>
      </div>
    </section>
  );
}